import React, { useMemo, useState } from 'react'
import { Vendor } from '../../../lib/types/Vendor'
import { ReactComponent as GoogleLogo } from '../../../images/google_ads_logo.svg'
import { ReactComponent as BrevoLogo } from '../../../images/vendors/brevo_logo.svg'
import { useController, useFormContext } from 'react-hook-form'
import { useGetVendorAdAccounts } from '../../../api/dealership/dsp/adAccounts/useGetVendorAdAccounts'
import { DualSelect } from '@demandlocal/components'
import { AnimatePresence, motion } from 'framer-motion'
import { ReactComponent as ChevronDown } from './../../../images/icons/chevron_down.svg'
import { ReactComponent as ChevronUp } from './../../../images/icons/chevron_up.svg'
import SelectedAccountsOptionsList from '../../../components/SelectedAccountsOptionsList'
import { getImageURL } from '../../../lib/utils/getImageURL'
import { VENDOR } from '../../../lib/utils/vendors'
import { useRefetchVendorAdAccounts } from '../../../api/dealership/dsp/adAccounts/useRefetchVendorAdAccounts'
import toast from 'react-hot-toast'
import { useDealership } from '../../../store/zustand/dealershipStore'

type Props = {
  vendor: Vendor
  mappedAudiences: Array<any>
  isLoading?: boolean
}

const ConnectedVendorAccountOptions = (props: Props): JSX.Element => {
  const { vendor, mappedAudiences, isLoading } = props

  const dealership = useDealership()
  const {
    mutateAsync: refetchVendorAdAccounts,
    isLoading: isRefetchingVendorAdAccounts,
  } = useRefetchVendorAdAccounts()
  const { data } = useGetVendorAdAccounts({
    vendor_id: vendor.id,
    force_refresh: false,
  })

  const {
    fieldState: { error },
  } = useController({
    name: `vendor_${vendor.id}`,
  })

  const vendorAccountsOptions = useMemo(() => {
    if (vendor.id === VENDOR.AmazonCognition) {
      return dealership?.amazon_ads_account_id
        ? [{ ...dealership?.amazon_ads_account_id }]
        : []
    }

    return data?.length > 0
      ? data.map((v) => ({
          label: `${v?.name ?? v?.label}`,
          value: v?.id ?? v?.value,
        }))
      : []
  }, [data, dealership?.amazon_ads_account_id, vendor.id])
  const [showAllMappedAudiences, setShowAllMappedAudiences] = useState(false)
  const { control } = useFormContext()

  const mappedAudiencesData = useMemo(() => {
    if (mappedAudiences.length > 0 && vendorAccountsOptions?.length > 0) {
      return mappedAudiences.map((mappedAudience) => {
        const account = vendorAccountsOptions.find(
          (account) =>
            String(account.value) === String(mappedAudience.vendor_account_vendor_id),
        )
        return {
          label: `${account?.label}`,
          value: mappedAudience.vendor_account_vendor_id,
        }
      })
    }
    return []
  }, [mappedAudiences, vendorAccountsOptions])

  return (
    <div className={`flex flex-row ${isLoading ? 'opacity-40' : ''}`}>
      <div>
        {vendor.name === 'Google Ads' && <GoogleLogo className={'w-16'} />}
        {vendor.id === VENDOR.Brevo && <BrevoLogo className={'w-16'} />}
        {vendor.name === 'Bing Ads' && (
          <img src={getImageURL('images/bing_ads_logo.png')} className={'w-16'} />
        )}
        {vendor.name === 'Facebook Ads' && (
          <img src={getImageURL('images/facebook_ads_logo.png')} className={'w-16'} />
        )}
        {vendor.name === 'Mailchimp' && (
          <img
            src={getImageURL('images/mailchimp_logo_small.png')}
            className={'w-16 h-16 object-cover'}
          />
        )}
        {vendor.id === VENDOR.TradeDesk && (
          <img
            src={getImageURL('images/vendors/ttd_small.png')}
            className={'w-16 object-cover'}
          />
        )}
        {vendor.id === VENDOR.Centro && (
          <img
            src={getImageURL('images/vendors/basis_logo.png')}
            className={'w-16 object-cover'}
          />
        )}
        {vendor.id === VENDOR.AmazonCognition && (
          <img
            src={getImageURL('images/vendors/amazon_ads_logo.png')}
            className={'w-16 object-cover'}
          />
        )}
      </div>
      <div className={'flex flex-col flex-1'}>
        <div className={'flex'}>
          <div className={'flex flex-row min-w-32'}>
            <div className={'pl-5 flex flex-col'}>
              <div className={'font-bold pb-2 text-xs'}>{vendor.name} account</div>
              <div className={'flex flex-col flex-shrink-0 pr-5'}>
                <DualSelect
                  showErrors={false}
                  optionsListTitle={'Choose account'}
                  selectedOptionsListTitle={'Selected accounts'}
                  buttonTitle={'Choose Accounts'}
                  control={control}
                  options={vendorAccountsOptions.filter(
                    (account) =>
                      !mappedAudiencesData
                        .map((v) => v.value)
                        .includes(String(account.value)),
                  )}
                  name={`vendor_${vendor.id}`}
                />
              </div>
            </div>
          </div>
          <div className={'flex flex-1 border-l-2 mt-6 pl-5'}>
            <SelectedAccountsOptionsList
              vendor_id={vendor.id}
              watchKey={`vendor_${vendor.id}`}
            />
          </div>
        </div>
        {mappedAudiencesData?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={'ml-5 pt-5 mt-5 border-t-2'}>
            <div className={'py-3 rounded-xl overflow-y-auto bg-cultured-gray'}>
              <div
                className={
                  'font-bold flex items-center justify-between px-3 cursor-pointer text-sm'
                }
                onClick={() => setShowAllMappedAudiences((prev) => !prev)}>
                <div className={'flex'}>
                  <div
                    className={
                      'rounded-full w-5 h-5 text-white text-center font-bold pt-0.5 text-[10px] bg-davy-gray'
                    }>
                    {mappedAudiencesData?.length}
                  </div>
                  <div className={'pl-3 text-davy-gray text-xs'}>
                    Currently linked accounts
                  </div>
                </div>
                {showAllMappedAudiences && <ChevronUp fill={'#09121F'} />}
                {!showAllMappedAudiences && <ChevronDown fill={'#09121F'} />}
              </div>
              <AnimatePresence initial={false}>
                {showAllMappedAudiences && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
                    className={'overflow-hidden'}>
                    <div
                      className={
                        'mt-3 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 px-3 '
                      }>
                      {mappedAudiencesData?.map((value, index: number) => {
                        return (
                          <motion.div
                            key={`${value.value}_${index}`}
                            className={
                              'flex flex-col p-2 min-w-32 rounded-lg min-w-[120px] bg-gainsboro-gray'
                            }>
                            <div className={'font-bold flex justify-between text-xs'}>
                              <span>{value.label}</span>
                            </div>
                            <span className={'font-medium pt-1 text-xs'}>
                              {value.value}
                            </span>
                          </motion.div>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
        <div className={'ml-5 pt-4'}>
          <div className={'font-medium flex flex-row mt-2 text-xs text-web-gray'}>
            Can't see your account?{' '}
            <span
              onClick={async (event) => {
                event.preventDefault()
                if (isRefetchingVendorAdAccounts) return
                const toastId = toast.loading('Refreshing list... This may take a while.')
                await refetchVendorAdAccounts({ vendor_id: vendor.id }).catch((err) => {
                  toast.error('Something went wrong. Please try again later.', {
                    id: toastId,
                  })
                })
                toast.success('List refreshed successfully!', { id: toastId })
              }}
              className={`${
                isRefetchingVendorAdAccounts
                  ? 'text-gainsboro-gray cursor-not-allowed'
                  : 'text-primary-persian-blue cursor-pointer hover:underline'
              } ml-2`}>
              Refresh list
            </span>
          </div>
          {vendor.id === VENDOR.Bing && mappedAudiencesData?.length === 0 && (
            <div
              className={
                'bg-secondary-sunglow/20 w-full p-4 rounded-md text-primary-xiketic text-sm mt-4'
              }>
              <p>
                Before you can upload customer list data via Bulk API, you must first
                create one customer list audience and accept the terms and conditions in
                the{' '}
                <a
                  href={'https://ui.ads.microsoft.com/campaign/vnext/audienceslibrary'}
                  target={'_blank'}
                  className={'text-primary-persian-blue font-bold'}
                  rel="noreferrer">
                  Microsoft Advertising UI
                </a>
                . The initial customer list doesn't need to contain any customer data, but
                you must select I ACCEPT.
              </p>
              <p>
                By selecting "I accept" you (1) agree that you are able to lawfully
                disclose audience details, which is personal data, to Microsoft and (2)
                accept the Customer Match Terms, the Microsoft Advertising Agreement, and
                the Microsoft Advertising policies. Microsoft will use the data that you
                upload in accordance with the Customer Match Terms.
              </p>
            </div>
          )}
          {vendor.name === 'Google Ads' && (
            <div className={'font-medium mt-2  text-xs text-web-gray'}>
              Note: It may take up to 48h for an audience to populate in your Google
              account.
            </div>
          )}
          {vendor.id === VENDOR.TradeDesk && (
            <div className={'font-medium mt-2 text-xs text-web-gray'}>
              Note: theTradeDesk requires an audience size of at least 5000 users.
            </div>
          )}
          {vendor.name === 'Mailchimp' && (
            <div className={'font-medium mt-2  text-xs text-web-gray'}>
              Note: Pushing members to an audience on Mailchimp requires you to create the
              list first on Mailchimp.
              <br />
              <a
                href={'https://admin.mailchimp.com/lists/'}
                target={'_blank'}
                rel="noreferrer">
                Create Mailchimp list
              </a>
            </div>
          )}
        </div>
        <div>
          <div
            className={
              'font-Spartan font-medium ml-5 mt-5  text-xs text-secondary-bittersweet-red'
            }>
            {error?.message}
          </div>
        </div>
      </div>
    </div>
  )
}
export default ConnectedVendorAccountOptions
